class KlaviyoBisComponent extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    const productId = this.dataset.productId;
    const canAddToCart = this.dataset.canAddToCart === 'true';

    this.#updateVisibility(canAddToCart);

    document.addEventListener(
      'variant:update',
      (event) => {
        if (event.detail.data.productId !== productId) return;

        const available = event.detail.resource && event.detail.resource.available;
        const klaviyoForm = this.querySelector('[data-variant-id]');

        this.#updateVisibility(available);

        if (klaviyoForm && event.detail.resource) {
          klaviyoForm.dataset.variantId = event.detail.resource.id;
        }
      },
      { signal: this.#abortController.signal }
    );
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#abortController = new AbortController();
  }

  #updateVisibility(available) {
    const buyButtons = this.closest('.buy-buttons-block')?.querySelector('[data-buy-buttons]');
    const klaviyoContainer = this.querySelector('[data-klaviyo-bis-container]');

    if (buyButtons) buyButtons.style.display = available ? '' : 'none';
    if (klaviyoContainer) klaviyoContainer.style.display = available ? 'none' : '';
  }
}

if (!customElements.get('klaviyo-bis-component')) {
  customElements.define('klaviyo-bis-component', KlaviyoBisComponent);
}

class KlaviyoBisFormComponent extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    const apiKey = this.dataset.apiKey;
    if (!apiKey) return;

    const form = this.querySelector('.klaviyo-bis__form-row');
    if (!form) return;

    const input = this.querySelector('.klaviyo-bis__input');
    const button = this.querySelector('.klaviyo-bis__button');
    const successMsg = this.querySelector('.klaviyo-bis__success');
    const errorMsg = this.querySelector('.klaviyo-bis__error');

    form.addEventListener(
      'submit',
      (e) => {
        e.preventDefault();
        const email = input.value.trim();
        if (!email) {
          input.focus();
          return;
        }

        const variantId = this.dataset.variantId;

        button.disabled = true;
        errorMsg.hidden = true;

        fetch(
          'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(apiKey),
          {
            method: 'POST',
            headers: { 'content-type': 'application/json', 'revision': '2024-10-15' },
            body: JSON.stringify({
              data: {
                type: 'subscription',
                attributes: {
                  profile: {
                    data: {
                      type: 'profile',
                      attributes: {
                        email,
                        properties: {
                          bis_product_title: this.dataset.productTitle,
                          bis_variant_title: this.dataset.variantTitle,
                          bis_variant_id: variantId,
                          bis_product_url: this.dataset.productUrl
                        }
                      }
                    }
                  }
                },
                relationships: {
                  list: {
                    data: {
                      type: 'list',
                      id: 'R3HrcH'
                    }
                  }
                }
              }
            })
          }
        )
          .then((res) => {
            if (res.ok || res.status === 202) {
              form.hidden = true;
              successMsg.hidden = false;
            } else {
              errorMsg.hidden = false;
              button.disabled = false;
            }
          })
          .catch(() => {
            errorMsg.hidden = false;
            button.disabled = false;
          });
      },
      { signal: this.#abortController.signal }
    );
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#abortController = new AbortController();
  }
}

if (!customElements.get('klaviyo-bis-form')) {
  customElements.define('klaviyo-bis-form', KlaviyoBisFormComponent);
}

class NotifyMeModal extends HTMLElement {
  #abortController = new AbortController();
  #movedOverlay = null;

  connectedCallback() {
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.#init();
    });
  }

  #init() {
    const overlay = this.querySelector('.notify-me-overlay');
    if (!overlay) return;

    // Move to <body> so position:fixed is relative to the viewport,
    // not broken by CSS transform/will-change on parent product card elements.
    document.body.appendChild(overlay);
    this.#movedOverlay = overlay;

    const open = () => {
      overlay.setAttribute('aria-hidden', 'false');
      document.documentElement.setAttribute('scroll-lock', '');
    };

    const close = () => {
      overlay.setAttribute('aria-hidden', 'true');
      document.documentElement.removeAttribute('scroll-lock');
    };

    const { signal } = this.#abortController;

    this.querySelector('.notify-me-btn')?.addEventListener('click', open, { signal });
    overlay.querySelector('.notify-me-modal__close')?.addEventListener('click', close, { signal });
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close();
    }, { signal });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && overlay.getAttribute('aria-hidden') !== 'true') close();
    }, { signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#abortController = new AbortController();
    // Return the overlay back into this element before it's removed.
    // This ensures the overlay travels with notify-me-modal when the grid
    // does innerHTML = '' + appendChild (e.g. paginated-list sold-out sorting),
    // so the next connectedCallback can find it again via querySelector.
    if (this.#movedOverlay) {
      this.appendChild(this.#movedOverlay);
      this.#movedOverlay = null;
    }
  }
}

if (!customElements.get('notify-me-modal')) {
  customElements.define('notify-me-modal', NotifyMeModal);
}
